import { normalizeInstanceUrl } from '../instance/config.ts';

/**
 * SponsorBlock, as data — the community database of segments (sponsor reads, intros, outros …)
 * keyed by video, served by `sponsor.ajay.app` or a self-hosted instance of the same server.
 *
 * This file is the *vocabulary*: which categories Visious speaks, what the reader can decide about
 * each, and the stored settings shape with its normalisers. What to do at a given playhead is
 * `decide.ts`; reading the wire format is `parse.ts`. Nothing here fetches, hashes or formats (R1,
 * R7) — the k-anonymity hash is the platform's job, because SHA-256 is a platform capability.
 */

/**
 * The eight *skippable* categories — the ones whose segments are `[start, end]` ranges with the
 * `skip` action, decided 2026-08-01. The database's other entries are deliberately not spoken:
 * `poi_highlight` is a point, `exclusive_access` labels the whole video, `chapter` is somebody
 * else's chapter list, and `mute`-action segments are a different behaviour wearing the same
 * categories. A payload row outside this list drops itself in the parser.
 */
export type SponsorCategory =
  | 'sponsor'
  | 'selfpromo'
  | 'interaction'
  | 'intro'
  | 'outro'
  | 'preview'
  | 'music_offtopic'
  | 'filler';

export const SPONSOR_CATEGORIES: readonly SponsorCategory[] = [
  'sponsor',
  'selfpromo',
  'interaction',
  'intro',
  'outro',
  'preview',
  'music_offtopic',
  'filler',
];

export function isSponsorCategory(value: unknown): value is SponsorCategory {
  return typeof value === 'string' && (SPONSOR_CATEGORIES as readonly string[]).includes(value);
}

/**
 * What happens when the playhead reaches a segment. `auto` jumps past it (the web layer shows an
 * un-skip toast); `toast` only offers a Skip button while inside it; `off` does nothing — the
 * segment can still be drawn on the bar and counted for chapter badges, which is why this is not
 * a boolean.
 */
export type SponsorSkipBehavior = 'auto' | 'toast' | 'off';

export interface SponsorCategorySettings {
  readonly behavior: SponsorSkipBehavior;
  /**
   * `#rrggbb`. A stored *value*, not a stylesheet literal — the reader can repaint each category,
   * so the colour travels with the settings document and is drawn via inline style, the same way
   * a storyboard crop is.
   */
  readonly color: string;
  readonly showInBar: boolean;
  readonly showInChapters: boolean;
}

/**
 * Every knob is the category's own (revised 2026-08-01, same day it shipped): there is no global
 * behaviour row and no global switches — the first cut had both, and a category answering
 * `default` to a row above it made every question about one segment a two-place lookup.
 */
export interface SponsorBlockSettings {
  /** Off by default: enabling it sends (hashed) lookups to a third-party service. */
  readonly enabled: boolean;
  /** A self-hosted server's address; `null` is the official one. */
  readonly apiUrl: string | null;
  readonly categories: Readonly<Record<SponsorCategory, SponsorCategorySettings>>;
}

/** The project's own default palette — SponsorBlock's official category colours. */
export const SPONSOR_CATEGORY_COLORS: Readonly<Record<SponsorCategory, string>> = {
  sponsor: '#00d400',
  selfpromo: '#ffff00',
  interaction: '#cc00ff',
  intro: '#00ffff',
  outro: '#0202ed',
  preview: '#008fd6',
  music_offtopic: '#ff9900',
  filler: '#7300ff',
};

function defaultCategory(category: SponsorCategory): SponsorCategorySettings {
  // Decided 2026-08-01: out of the box only sponsor reads are shown and skipped. The other seven
  // start fully off — not skipped, not drawn — because a bar that arrives pre-painted with eight
  // colours reads as noise, and skipping an intro nobody asked about reads as a player glitch.
  const on = category === 'sponsor';
  return {
    behavior: on ? 'auto' : 'off',
    color: SPONSOR_CATEGORY_COLORS[category],
    showInBar: on,
    showInChapters: on,
  };
}

function defaultCategories(): Readonly<Record<SponsorCategory, SponsorCategorySettings>> {
  const entries = {} as Record<SponsorCategory, SponsorCategorySettings>;
  for (const category of SPONSOR_CATEGORIES) entries[category] = defaultCategory(category);
  return entries;
}

export const DEFAULT_SPONSORBLOCK_SETTINGS: SponsorBlockSettings = {
  enabled: false,
  apiUrl: null,
  categories: defaultCategories(),
};

// ---------------------------------------------------------------------------
// Normalisers — total (R4): the input is a stored blob or a hand-edited one
// ---------------------------------------------------------------------------

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeBehavior(input: unknown, fallback: SponsorSkipBehavior): SponsorSkipBehavior {
  return input === 'auto' || input === 'toast' || input === 'off' ? input : fallback;
}

/** `#rrggbb` only, lowercased — the one shape both a colour input and an inline style speak. */
export function normalizeSponsorColor(input: unknown, fallback: string): string {
  return typeof input === 'string' && /^#[0-9a-fA-F]{6}$/.test(input.trim())
    ? input.trim().toLowerCase()
    : fallback;
}

/**
 * A stored service address → a canonical base URL or `null` (the official service). Through the
 * instance normaliser deliberately: a SponsorBlock server is addressed exactly like an Invidious
 * one — scheme, host, optional base path, nothing after it.
 */
export function normalizeSponsorApiUrl(input: unknown): string | null {
  if (typeof input !== 'string' || input.trim() === '') return null;
  const result = normalizeInstanceUrl(input);
  return result.ok ? result.value : null;
}

/**
 * The first cut (earlier the same day) stored a global behaviour plus two global switches, and a
 * category could answer `default` / AND against them. A blob written by that build still means
 * what it meant: the globals are resolved into each category here, once, at read time.
 */
interface LegacyGlobals {
  readonly behavior: SponsorSkipBehavior;
  readonly showInBar: boolean;
  readonly showInChapters: boolean;
}

function normalizeCategorySettings(
  input: unknown,
  category: SponsorCategory,
  legacy: LegacyGlobals,
): SponsorCategorySettings {
  const fallback = defaultCategory(category);
  if (!isRecord(input)) return fallback;
  return {
    behavior:
      input['behavior'] === 'default'
        ? legacy.behavior
        : normalizeBehavior(input['behavior'], fallback.behavior),
    color: normalizeSponsorColor(input['color'], fallback.color),
    showInBar:
      legacy.showInBar &&
      (typeof input['showInBar'] === 'boolean' ? input['showInBar'] : fallback.showInBar),
    showInChapters:
      legacy.showInChapters &&
      (typeof input['showInChapters'] === 'boolean' ? input['showInChapters'] : fallback.showInChapters),
  };
}

export function normalizeSponsorBlockSettings(input: unknown): SponsorBlockSettings {
  if (!isRecord(input)) return DEFAULT_SPONSORBLOCK_SETTINGS;
  // A new-format blob has none of the three keys, so the legacy globals collapse to identities.
  const legacy: LegacyGlobals = {
    behavior: normalizeBehavior(input['behavior'], 'auto'),
    showInBar: input['showInBar'] !== false,
    showInChapters: input['showInChapters'] !== false,
  };
  const categoriesInput = isRecord(input['categories']) ? input['categories'] : {};
  const categories = {} as Record<SponsorCategory, SponsorCategorySettings>;
  for (const category of SPONSOR_CATEGORIES) {
    categories[category] = normalizeCategorySettings(categoriesInput[category], category, legacy);
  }
  return {
    enabled: input['enabled'] === true,
    apiUrl: normalizeSponsorApiUrl(input['apiUrl']),
    categories,
  };
}

// ---------------------------------------------------------------------------
// A segment, raw and resolved
// ---------------------------------------------------------------------------

/** One database row, as parsed — what the video *has*, before the settings have had their say. */
export interface SponsorSegment {
  readonly uuid: string;
  readonly category: SponsorCategory;
  readonly startSec: number;
  readonly endSec: number;
}

/** A segment with the settings already applied — what this reader wants *done* about it. */
export interface ResolvedSponsorSegment extends SponsorSegment {
  readonly behavior: SponsorSkipBehavior;
  readonly color: string;
  readonly showInBar: boolean;
  readonly showInChapters: boolean;
}

/**
 * Apply the settings once, up front, so the per-tick decision (`decide.ts`) never re-derives them.
 * A segment resolves to `off`/hidden rather than being dropped: "do nothing about it" and "it is
 * not there" are different answers, and the chapter badges need the union while the bar does not.
 */
export function resolveSponsorSegments(
  settings: SponsorBlockSettings,
  segments: readonly SponsorSegment[],
): readonly ResolvedSponsorSegment[] {
  return segments.map((segment) => {
    const category = settings.categories[segment.category];
    return {
      ...segment,
      behavior: category.behavior,
      color: category.color,
      showInBar: category.showInBar,
      showInChapters: category.showInChapters,
    };
  });
}
